/* ============================================================
   Wardrobe Designer — controller

   Owns state, builds every control from WD_CATALOGUE, drives the SVG
   through WD_RENDER, and posts the finished design plus contact details
   to the CRM. There is no pricing here and there never should be: the
   enquiry goes to a human who quotes it.

   Depends on (loaded before this file, all plain globals):
     WD_CATALOGUE, WD_GEOM, WD_RENDER
   and window.WD_SETTINGS written by sections/wardrobe-designer.liquid.

   Vanilla ES2020, iOS 15 Safari safe. AU English.
   ============================================================ */
(function () {
  'use strict';

  var STORAGE_KEY = 'wd:design:v1';
  var MAX_AGE_MS = 1209600000; // 14 days
  var ATTR_MAX_AGE_MS = 7776e6; // 90 days, matches the Goober attribution TTL
  var TOTAL_STEPS = 5;

  /* ---------------------------------------------------------
     Small helpers
     --------------------------------------------------------- */
  function q(root, sel) { return root.querySelector(sel); }
  function qa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function clamp(n, lo, hi) { return Math.min(hi, Math.max(lo, n)); }
  function roundTo(n, step) { return Math.round(n / step) * step; }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function tpl(str, map) {
    return String(str || '').replace(/\[(\w+)\]/g, function (m, k) {
      return Object.prototype.hasOwnProperty.call(map, k) ? map[k] : m;
    });
  }
  /* Every analytics call is guarded: a blocked library must never break the page. */
  function track(name, params) {
    try { if (typeof window.gtag === 'function') window.gtag('event', name, params || {}); } catch (e) {}
  }
  function readAttr() {
    try {
      var raw = localStorage.getItem('goob_attr');
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (parsed && typeof parsed.ts === 'number' && (Date.now() - parsed.ts) < ATTR_MAX_AGE_MS) return parsed;
    } catch (e) {}
    return null;
  }

  /* base64url so a design survives being pasted into a message */
  function b64urlEncode(str) {
    try {
      return btoa(unescape(encodeURIComponent(str))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    } catch (e) { return ''; }
  }
  function b64urlDecode(str) {
    try {
      var s = String(str).replace(/-/g, '+').replace(/_/g, '/');
      while (s.length % 4) s += '=';
      return decodeURIComponent(escape(atob(s)));
    } catch (e) { return ''; }
  }

  function init(root) {
    if (!root || root.getAttribute('data-wd-ready') === '1') return;
    var cat = window.WD_CATALOGUE;
    var GEOM = window.WD_GEOM;
    var REND = window.WD_RENDER;
    if (!cat || !GEOM || !REND) return; // modules missing: static shell stands, noscript copy applies
    root.setAttribute('data-wd-ready', '1');

    var S = window.WD_SETTINGS || {};
    var endpoint = root.getAttribute('data-wd-endpoint') || '';
    var crmKey = root.getAttribute('data-wd-key') || '';

    var frame = q(root, '[data-wd-frame]');
    var live = q(root, '[data-wd-live]');
    var panelNote = q(root, '[data-wd-panelnote]');
    var specEl = q(root, '[data-wd-spec]');
    var form = q(root, '[data-wd-form]');
    var backBtn = q(root, '[data-wd-back]');
    var nextBtn = q(root, '[data-wd-next]');
    var nav = q(root, '[data-wd-nav]');

    /* ---------------------------------------------------------
       State
       --------------------------------------------------------- */
    function defaultState() {
      var W = cat.opening.width.default;
      var layout = pickDefaultLayout(W);
      return {
        v: 1,
        opening: { w: W, h: cat.opening.height.default, d: cat.opening.depth.options[0].value },
        doors: { type: 'sliding', count: GEOM.bandCount(W), insert: 'mirror', frame: 'matt-natural' },
        internals: {
          topShelf: cat.topShelf.default,
          layout: layout ? layout.id : null,
          sections: layout ? GEOM.realiseLayout(layout.recipe, W, cat) : []
        },
        install: true,
        notes: ''
      };
    }
    function pickDefaultLayout(W) {
      var avail = GEOM.availableLayouts(W, cat);
      if (!avail.length) return null;
      for (var i = 0; i < avail.length; i++) if (avail[i].default) return avail[i];
      return avail[0];
    }

    var state = defaultState();
    var pendingNote = '';
    var ui = { step: 1, view: 'int', selected: -1, started: false, startedAt: 0, maxStep: 1 };

    /* ---------------------------------------------------------
       Share link: terse tuple then base64url, per the plan
       --------------------------------------------------------- */
    function serialise() {
      var payload = {
        v: 1,
        o: [state.opening.w, state.opening.h, state.opening.d],
        dr: [state.doors.type, state.doors.insert, state.doors.frame, state.doors.count],
        i: [state.internals.topShelf ? 1 : 0].concat(state.internals.sections.map(function (s) {
          return [s.t, s.w, s.t === 'drawer' ? s.drawers : (s.t === 'shelf' ? s.shelves : 0), s.hangAbove ? 1 : 0];
        })),
        x: [state.install ? 1 : 0]
      };
      return b64urlEncode(JSON.stringify(payload));
    }
    function deserialise(code) {
      var raw = b64urlDecode(code);
      if (!raw) return null;
      var p;
      try { p = JSON.parse(raw); } catch (e) { return null; }
      if (!p || p.v !== 1 || !p.o || !p.dr || !p.i) return null;
      try {
        var st = defaultState();
        st.opening.w = clamp(+p.o[0] || st.opening.w, cat.opening.width.min, cat.opening.width.max);
        st.opening.h = clamp(+p.o[1] || st.opening.h, cat.opening.height.min, cat.opening.height.max);
        st.opening.d = +p.o[2] === 580 ? 580 : 440;
        if (typeGeom(p.dr[0])) st.doors.type = p.dr[0];
        if (findBy(cat.doors.inserts, p.dr[1])) st.doors.insert = p.dr[1];
        if (findBy(cat.doors.frames, p.dr[2])) st.doors.frame = p.dr[2];
        var allowed = GEOM.allowedDoorCounts(st.opening.w, st.doors.type);
        st.doors.count = allowed.indexOf(+p.dr[3]) > -1 ? +p.dr[3] : GEOM.bandCount(st.opening.w);
        st.internals.topShelf = !!p.i[0];
        var secs = [];
        for (var k = 1; k < p.i.length; k++) {
          var t = p.i[k][0];
          if (!cat.sections[t]) continue;
          var sec = { t: t, w: clamp(+p.i[k][1] || cat.sections[t].def, cat.sections[t].min, cat.sections[t].max) };
          if (t === 'drawer') { sec.drawers = +p.i[k][2] || 4; sec.hangAbove = !!p.i[k][3]; }
          if (t === 'shelf') sec.shelves = +p.i[k][2] || 5;
          secs.push(sec);
        }
        if (secs.length) {
          st.internals.sections = normaliseSections(secs, st.opening.w);
          st.internals.layout = null;
        }
        if (p.x) st.install = !!p.x[0];
        return st;
      } catch (e) { return null; }
    }
    function findBy(arr, val) {
      for (var i = 0; i < arr.length; i++) if (arr[i].value === val) return arr[i];
      return null;
    }
    function typeGeom(v) { return findBy(cat.doors.types, v); }

    function shareUrl() {
      return location.origin + location.pathname + '#d=' + serialise();
    }

    /* ---------------------------------------------------------
       Persistence
       --------------------------------------------------------- */
    var persistTimer = null;
    var persistBlocked = false;
    function persist() {
      if (persistBlocked) return;
      if (persistTimer) clearTimeout(persistTimer);
      persistTimer = setTimeout(function () {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ v: 1, savedAt: Date.now(), design: state }));
        } catch (e) {}
      }, 300);
    }
    function restore() {
      // A shared link always beats whatever is in storage.
      var m = location.hash.match(/[#&]d=([A-Za-z0-9\-_]+)/);
      if (m) {
        var fromUrl = deserialise(m[1]);
        if (fromUrl) { state = fromUrl; return false; }
      }
      try {
        var raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return false;
        var saved = JSON.parse(raw);
        if (!saved || saved.v !== 1 || !saved.design) return false;
        if (Date.now() - (saved.savedAt || 0) > MAX_AGE_MS) return false;
        var d = saved.design;
        if (!d.opening || !d.internals || !d.internals.sections) return false;
        state = d;
        /* A save written by an older catalogue can hold widths or counts that
           are no longer legal, so repair rather than trust it. */
        normaliseSections(state.internals.sections, state.opening.w);
        var allowedNow = GEOM.allowedDoorCounts(state.opening.w, state.doors.type);
        if (allowedNow.indexOf(state.doors.count) === -1) state.doors.count = GEOM.bandCount(state.opening.w);
        return true;
      } catch (e) { return false; }
    }

    /* ---------------------------------------------------------
       Basic 3D: the drawing is the front face of a box, with two side
       faces behind it in the same 3D space. Dragging spins it on the Y
       axis. Not a 3D engine, and deliberately not one: the SVG stays the
       single source of truth and nothing extra is downloaded.
       --------------------------------------------------------- */
    var spin = 0;            // degrees, clamped
    var SPIN_MAX = 38;
    var dragFrom = null;
    var reduceMotion = false;
    try { reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

    var scene = q(root, '[data-wd-scene]');
    function applySpin() {
      if (!scene) return;
      scene.style.transform = 'rotateY(' + spin.toFixed(2) + 'deg)';

      /* The SVG letterboxes the robe inside its viewBox, so the side faces are
         placed from the real carcass box rather than fixed percentages of the
         frame. Without this they float outside the cabinet at most sizes. */
      var box = (REND.carcassBox ? REND.carcassBox(state.opening.w, state.opening.h) : null) ||
        { leftPct: 17, rightPct: 15, topPct: 8, bottomPct: 12 };
      var frameW = frame.getBoundingClientRect().width || 600;
      var depthMm = state.opening.d === 580 ? 580 : 440;
      /* Depth in the same pixel scale as the drawing, so a deep robe reads deeper. */
      var pxPerMm = (frameW * (100 - box.leftPct - box.rightPct) / 100) / Math.max(1, state.opening.w);
      var depth = Math.max(14, Math.min(70, depthMm * pxPerMm));

      var sides = qa(root, '[data-wd-side]');
      sides.forEach(function (el) {
        var isLeft = el.getAttribute('data-wd-side') === 'left';
        var dir = isLeft ? -1 : 1;
        el.style.width = depth + 'px';
        el.style.top = box.topPct + '%';
        el.style.bottom = box.bottomPct + '%';
        if (isLeft) { el.style.left = box.leftPct + '%'; el.style.right = 'auto'; }
        else { el.style.right = box.rightPct + '%'; el.style.left = 'auto'; }
        el.style.transformOrigin = isLeft ? 'left center' : 'right center';
        el.style.transform = 'rotateY(' + (dir * 90) + 'deg)';
      });
      /* Sides only make sense once the box is turned; fade them in with the angle. */
      var reveal = Math.min(1, Math.abs(spin) / 12);
      sides.forEach(function (el) {
        var isLeading = (spin < 0) === (el.getAttribute('data-wd-side') === 'left');
        el.style.opacity = isLeading ? reveal.toFixed(2) : '0';
      });
    }
    function setSpin(deg) {
      spin = clamp(deg, -SPIN_MAX, SPIN_MAX);
      applySpin();
    }
    if (scene && !reduceMotion) {
      var stageEl = q(root, '[data-wd-frame]');
      var startSpin = 0;
      var onMove = function (clientX) {
        if (dragFrom === null) return;
        setSpin(startSpin + (clientX - dragFrom) * 0.25);
      };
      stageEl.addEventListener('pointerdown', function (e) {
        if (e.target.closest && e.target.closest('[data-sec]')) return; // taps still select a part
        dragFrom = e.clientX;
        startSpin = spin;
        stageEl.classList.add('is-dragging');
        try { stageEl.setPointerCapture(e.pointerId); } catch (err) {}
      });
      stageEl.addEventListener('pointermove', function (e) {
        if (dragFrom === null) return;
        e.preventDefault();
        onMove(e.clientX);
      });
      ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (ev) {
        stageEl.addEventListener(ev, function () {
          dragFrom = null;
          stageEl.classList.remove('is-dragging');
        });
      });
      /* Keyboard parity for the drag. */
      stageEl.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { setSpin(spin - 6); e.preventDefault(); }
        if (e.key === 'ArrowRight') { setSpin(spin + 6); e.preventDefault(); }
        if (e.key === 'Home') { setSpin(0); e.preventDefault(); }
      });
    }

    /* ---------------------------------------------------------
       Render, rAF gated so a burst of input coalesces into one paint
       --------------------------------------------------------- */
    var rafId = null;
    function scheduleRender() {
      /* A hidden page never runs requestAnimationFrame callbacks. Queueing one
         would leave rafId set forever and silently block every later render, so
         paint straight away instead and let the coalescing resume when visible. */
      if (typeof document !== 'undefined' && document.hidden) {
        rafId = null;
        paint();
        return;
      }
      if (rafId) return;
      rafId = requestAnimationFrame(function () {
        rafId = null;
        paint();
      });
    }
    /* Belt and braces: if anything did wedge while hidden, clear it on return. */
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', function () {
        if (!document.hidden) { rafId = null; paint(); }
      });
    }
    function paint() {
      var face = q(root, '[data-wd-face]') || frame;
      face.innerHTML = REND.renderElevation(state, ui.view, {
        theme: 'customer',
        showDims: true,
        selected: ui.selected
      });
      frame.setAttribute('aria-label', REND.ariaLabel(state, cat));
      applySpin();
      paintPanelNote();
      if (specEl) specEl.textContent = GEOM.describe(state, cat);
      /* Only save once the visitor has actually touched something, or every
         second page view greets a brand new visitor with "welcome back". */
      if (ui.started) persist();
    }
    function paintPanelNote() {
      if (!panelNote) return;
      var g = GEOM.doorGeom(state.opening.w, state.opening.h, state.doors.type, state.doors.count);
      var t = typeGeom(state.doors.type);
      panelNote.innerHTML = tpl(esc(S.panelNote || ''), {
        n: '<b>' + g.n + '</b>',
        type: esc((t ? t.label : state.doors.type).toLowerCase()),
        pw: '<b>' + Math.round(g.pw) + '</b>',
        ph: '<b>' + Math.round(g.ph) + '</b>'
      });
    }

    var liveTimer = null;
    function announce(msg) {
      if (!live) return;
      if (liveTimer) clearTimeout(liveTimer);
      liveTimer = setTimeout(function () { live.textContent = msg; }, 800);
    }

    function setState(patch) {
      if (patch) for (var k in patch) if (Object.prototype.hasOwnProperty.call(patch, k)) state[k] = patch[k];
      if (!ui.started) {
        ui.started = true;
        ui.startedAt = Date.now();
        track('wd_start', {});
      }
      scheduleRender();
    }

    /* Width changed: re-realise the layout so sections always sum to W. */
    function reflowSections() {
      var W = state.opening.w;
      /* A count that no longer fits the new width snaps back to the catalogue band. */
      var allowedCounts = GEOM.allowedDoorCounts(W, state.doors.type);
      if (allowedCounts.indexOf(state.doors.count) === -1) state.doors.count = GEOM.bandCount(W);
      var lay = state.internals.layout ? findLayout(state.internals.layout) : null;
      if (lay && GEOM.layoutIsValid(lay, W, cat)) {
        state.internals.sections = GEOM.realiseLayout(lay.recipe, W, cat);
      } else if (state.internals.layout === null && state.internals.sections.length) {
        /* The customer has hand-edited the internals, so keep their parts and
           just refit them to the new width. Replacing them with a stock layout
           threw away work with no warning. */
        normaliseSections(state.internals.sections, W);
      } else {
        var next = pickDefaultLayout(W);
        if (next) {
          state.internals.layout = next.id;
          state.internals.sections = GEOM.realiseLayout(next.recipe, W, cat);
        }
      }
      if (ui.selected >= state.internals.sections.length) ui.selected = -1;
    }
    function findLayout(id) {
      for (var i = 0; i < cat.layouts.length; i++) if (cat.layouts[i].id === id) return cat.layouts[i];
      return null;
    }

    /* ---------------------------------------------------------
       Control builders — everything comes from the catalogue
       --------------------------------------------------------- */
    function radioGroup(name, items, currentValue, onPick, opts) {
      opts = opts || {};
      var wrap = document.createElement('div');
      wrap.className = opts.className || 'wd__chips';
      wrap.setAttribute('role', 'radiogroup');
      if (opts.label) wrap.setAttribute('aria-label', opts.label);
      items.forEach(function (item) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = opts.chipClass || 'wd__chip';
        b.setAttribute('role', 'radio');
        var on = item.value === currentValue;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
        b.innerHTML = opts.render ? opts.render(item) :
          '<span>' + esc(item.label) + '</span>' + (item.sub ? '<span class="wd__chip-sub">' + esc(item.sub) + '</span>' : '');
        b.wdValue = item.value;
        b.addEventListener('click', function () { onPick(item); });
        wrap.appendChild(b);
      });
      /* Re-mark without rebuilding the DOM, so focus survives a pick. */
      wrap.wdSetChecked = function (value) {
        qa(wrap, '[role="radio"]').forEach(function (b) {
          var on = b.wdValue === value;
          b.setAttribute('aria-checked', on ? 'true' : 'false');
          b.tabIndex = on ? 0 : -1;
        });
      };
      wrap.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
        var btns = qa(wrap, '[role="radio"]');
        var i = btns.indexOf(document.activeElement);
        if (i < 0) return;
        e.preventDefault();
        var dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1;
        var nx = (i + dir + btns.length) % btns.length;
        btns[nx].focus();
        btns[nx].click();
      });
      return wrap;
    }

    function numberRow(labelText, value, conf, onChange) {
      var row = document.createElement('div');
      row.className = 'wd__num';
      var inputId = 'wd-num-' + labelText.toLowerCase().replace(/[^a-z]+/g, '');
      row.innerHTML =
        '<label class="wd__numlabel" for="' + inputId + '">' + esc(labelText) + '</label>' +
        '<button type="button" class="wd__stepper" data-d="-1" aria-label="Decrease ' + esc(labelText) + '">&minus;</button>' +
        '<input class="wd__input" id="' + inputId + '" type="number" inputmode="numeric" value="' + value + '" min="' + conf.min + '" max="' + conf.max + '" step="' + conf.step + '">' +
        '<button type="button" class="wd__stepper" data-d="1" aria-label="Increase ' + esc(labelText) + '">+</button>';
      var input = q(row, 'input');
      var note = document.createElement('p');
      note.className = 'wd__hint wd__hint--clamp';
      note.hidden = true;

      function commit(raw) {
        var n = parseInt(raw, 10);
        if (isNaN(n)) { input.value = value; return; }
        var clamped = clamp(roundTo(n, conf.step), conf.min, conf.max);
        /* Only a genuine range hit deserves the limit note; plain rounding to
           the 10mm step is not something to explain. */
        var msg = '';
        if (n > conf.max) msg = tpl(S.clampNote || '', { limit: conf.max });
        else if (n < conf.min) msg = tpl(S.clampNote || '', { limit: conf.min });
        note.textContent = msg;
        note.hidden = !msg;
        value = clamped;
        input.value = clamped;
        onChange(clamped, msg);
      }
      qa(row, '.wd__stepper').forEach(function (b) {
        b.addEventListener('click', function () {
          commit((parseInt(input.value, 10) || conf.min) + (+b.getAttribute('data-d')) * conf.step);
        });
      });
      input.addEventListener('blur', function () { commit(input.value); });
      input.addEventListener('change', function () { commit(input.value); });

      var box = document.createElement('div');
      box.appendChild(row);
      box.appendChild(note);
      return box;
    }

    /* ---- Step 1: size ---- */
    function selectRow(labelText, conf, current, onPick) {
      var wrap = document.createElement('div');
      wrap.className = 'wd__field';
      var id = 'wd-sel-' + labelText.toLowerCase().replace(/[^a-z]+/g, '');
      var isPreset = false;
      conf.presets.forEach(function (p) { if (p.value === current) isPreset = true; });

      var opts = conf.presets.map(function (p) {
        return '<option value="' + p.value + '"' + (p.value === current ? ' selected' : '') + '>' + esc(p.label) + ' · ' + p.value + 'mm</option>';
      }).join('');
      opts += '<option value="custom"' + (isPreset ? '' : ' selected') + '>Custom size…</option>';

      wrap.innerHTML = '<label class="wd__label" for="' + id + '">' + esc(labelText) + '</label>' +
        '<select id="' + id + '" class="wd__select">' + opts + '</select>';

      var custom = document.createElement('div');
      custom.className = 'wd__custom' + (isPreset ? '' : ' is-open');
      custom.appendChild(numberRow(labelText + ' (mm)', current, conf, function (v, msg) { onPick(v, msg); }));

      q(wrap, 'select').addEventListener('change', function (e) {
        if (e.target.value === 'custom') { custom.classList.add('is-open'); return; }
        custom.classList.remove('is-open');
        onPick(parseInt(e.target.value, 10));
      });

      var box = document.createElement('div');
      box.appendChild(wrap);
      box.appendChild(custom);
      return box;
    }

    function mountSize() {
      var host = q(root, '[data-wd-mount="size"]');
      host.innerHTML = '';
      host.appendChild(selectRow('Opening width', cat.opening.width, state.opening.w, function (v, note) {
        state.opening.w = v;
        reflowSections();
        setState();
        pendingNote = note || '';
        rebuild('size');
        announce('Opening now ' + v + ' millimetres wide');
      }));
      host.appendChild(selectRow('Opening height', cat.opening.height, state.opening.h, function (v, note) {
        state.opening.h = v;
        setState();
        pendingNote = note || '';
        rebuild('size');
        announce('Opening now ' + v + ' millimetres high');
      }));
      /* Survives the rebuild that the change itself triggers. */
      if (pendingNote) {
        var carry = document.createElement('p');
        carry.className = 'wd__hint wd__hint--clamp';
        carry.textContent = pendingNote;
        host.appendChild(carry);
        pendingNote = '';
      }
    }

    function fieldset(legend, a, b) {
      var fs = document.createElement('fieldset');
      fs.className = 'wd__group';
      var lg = document.createElement('legend');
      lg.className = 'wd__legend';
      lg.textContent = legend;
      fs.appendChild(lg);
      if (a) fs.appendChild(a);
      if (b) fs.appendChild(b);
      return fs;
    }

    /* ---- Step 2: depth ---- */
    function mountDepth() {
      var host = q(root, '[data-wd-mount="depth"]');
      host.innerHTML = '';
      var items = cat.opening.depth.options.map(function (o) { return { value: o.value, label: o.label, sub: o.sub }; });
      host.appendChild(radioGroup('d', items, state.opening.d, function (item) {
        state.opening.d = item.value;
        setState();
        withFocusKept(function () { rebuild('depth'); });
        announce(item.label + ' selected');
      }, { label: 'Depth', className: 'wd__chips wd__chips--block' }));
    }

    /* ---- Step 3: doors ---- */
    function mountDoors() {
      var host = q(root, '[data-wd-mount="doors"]');
      host.innerHTML = '';

      /* Sliding is the only type sold, so there is no type picker. What the
         customer does choose is how many panels, limited to counts we can
         actually build at their width. */
      var counts = GEOM.allowedDoorCounts(state.opening.w, state.doors.type);
      if (counts.indexOf(state.doors.count) === -1) state.doors.count = GEOM.bandCount(state.opening.w);

      var band = GEOM.bandCount(state.opening.w);
      var countItems = counts.map(function (n) {
        var g = GEOM.doorGeom(state.opening.w, state.opening.h, state.doors.type, n);
        /* Only badge the band default as usual when it is genuinely buildable.
           Very narrow openings fall below the 600mm panel minimum, and calling
           that "our usual" would be plainly wrong. */
        var usual = (n === band && !g.outOfRange) ? ' · our usual' : '';
        var flag = g.outOfRange ? ' · we will confirm this one' : '';
        return { value: n, label: n + ' doors', sub: Math.round(g.pw) + 'mm each' + usual + flag };
      });
      host.appendChild(fieldset('How many doors', radioGroup('dn', countItems, state.doors.count, function (item) {
        state.doors.count = item.value;
        setState();
        withFocusKept(function () { rebuild('doors'); });
        announce(item.value + ' doors');
      })));

      var insItems = cat.doors.inserts.map(function (i) {
        return { value: i.value, label: i.label, sub: i.sub, fill: i.fill };
      });
      host.appendChild(fieldset('What the panels are made from', radioGroup('ins', insItems, state.doors.insert, function (item) {
        state.doors.insert = item.value;
        setState();
        withFocusKept(function () { rebuild('doors'); });
        announce(item.label + ' panels');
      }, {
        label: 'Panel material',
        className: 'wd__swatches',
        chipClass: 'wd__swatch',
        render: function (item) {
          return '<span class="wd__swatch-dot" style="background:' + esc(item.fill) + '"></span><span>' + esc(item.label) + '</span>';
        }
      })));

      var frItems = cat.doors.frames.map(function (f) { return { value: f.value, label: f.label, swatch: f.swatch }; });
      host.appendChild(fieldset('Frame colour', radioGroup('fr', frItems, state.doors.frame, function (item) {
        state.doors.frame = item.value;
        setState();
        withFocusKept(function () { rebuild('doors'); });
        announce(item.label + ' frame');
      }, {
        label: 'Frame colour',
        className: 'wd__swatches',
        chipClass: 'wd__swatch',
        render: function (item) {
          return '<span class="wd__swatch-dot" style="background:' + esc(item.swatch) + '"></span><span>' + esc(item.label) + '</span>';
        }
      })));
    }

    /* ---- Step 4: inside ---- */
    function mountInside() {
      var host = q(root, '[data-wd-mount="inside"]');
      host.innerHTML = '';

      var avail = GEOM.availableLayouts(state.opening.w, cat);
      var grid = document.createElement('div');
      grid.className = 'wd__layouts';
      grid.setAttribute('role', 'radiogroup');
      grid.setAttribute('aria-label', 'Inside layout');

      avail.forEach(function (lay) {
        var preview = { opening: state.opening, doors: state.doors, internals: { topShelf: state.internals.topShelf, sections: GEOM.realiseLayout(lay.recipe, state.opening.w, cat) } };
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'wd__layout';
        b.setAttribute('role', 'radio');
        var on = lay.id === state.internals.layout;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
        b.innerHTML =
          '<span class="wd__layout-thumb">' + REND.renderElevation(preview, 'int', { theme: 'customer', thumbnail: true }) + '</span>' +
          '<span class="wd__layout-name">' + esc(lay.label) + '</span>' +
          '<span class="wd__layout-sub">' + esc(lay.sub) + '</span>';
        b.addEventListener('click', function () {
          state.internals.layout = lay.id;
          state.internals.sections = GEOM.realiseLayout(lay.recipe, state.opening.w, cat);
          ui.selected = -1;
          setState();
          track('wd_layout', { layout: lay.id });
          withFocusKept(function () { rebuild('inside'); });
          announce(lay.label + ' layout');
        });
        grid.appendChild(b);
      });
      /* Same roving-focus behaviour as radioGroup, which this grid predates. */
      grid.addEventListener('keydown', function (e) {
        if (['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].indexOf(e.key) === -1) return;
        var cards = qa(grid, '[role="radio"]');
        var at = cards.indexOf(document.activeElement);
        if (at < 0) return;
        e.preventDefault();
        var dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1;
        var nx = (at + dir + cards.length) % cards.length;
        cards[nx].focus();
        cards[nx].click();
      });
      /* With no layout selected every card was tabIndex -1, leaving the group
         unreachable by keyboard. Guarantee exactly one tab stop. */
      (function () {
        var cards = qa(grid, '[role="radio"]');
        if (cards.length && !cards.some(function (c) { return c.tabIndex === 0; })) cards[0].tabIndex = 0;
      })();
      host.appendChild(fieldset('Start with a layout', grid));

      /* Each part of the robe gets its own editable row, so it is obvious the
         internals are customisable one bay at a time. Tapping the drawing does
         the same thing; these rows are the keyboard-reachable equivalent. */
      var hint = document.createElement('p');
      hint.className = 'wd__editshint';
      hint.textContent = S.editsHint || 'Now customise each part. Tap a part below, or tap it in the drawing.';
      host.appendChild(hint);

      var list = document.createElement('ul');
      list.className = 'wd__seclist';
      state.internals.sections.forEach(function (sec, i) {
        var li = document.createElement('li');
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'wd__secrow';
        b.setAttribute('aria-pressed', ui.selected === i ? 'true' : 'false');
        var detail = sec.t === 'drawer' ? (sec.drawers + ' drawers' + (sec.hangAbove ? ', hanging above' : ', shelves above'))
          : sec.t === 'shelf' ? (sec.shelves + ' shelves')
          : (sec.w + 'mm wide');
        b.innerHTML =
          '<span class="wd__secrow-ico"><svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" width="20" height="20"><use href="#wd-i-' + esc(sec.t) + '"></use></svg></span>' +
          '<span class="wd__secrow-body">' +
            '<span class="wd__secrow-name">Part ' + (i + 1) + ' · ' + esc(cat.sections[sec.t].label) + '</span>' +
            '<span class="wd__secrow-detail">' + esc(detail) + '</span>' +
          '</span>' +
          '<span class="wd__secrow-edit">' + esc(ui.selected === i ? 'Close' : 'Change') + '</span>';
        b.addEventListener('click', function () {
          ui.selected = (ui.selected === i) ? -1 : i;
          setState();
          rebuild('inside');
        });
        li.appendChild(b);
        list.appendChild(li);
      });
      host.appendChild(list);

      if (ui.selected > -1 && state.internals.sections[ui.selected]) host.appendChild(tweakPanel(ui.selected));

      var tsWrap = document.createElement('label');
      tsWrap.className = 'wd__toggle';
      tsWrap.innerHTML = '<input type="checkbox"' + (state.internals.topShelf ? ' checked' : '') + '><span>' + esc(cat.topShelf.label) + '</span>';
      q(tsWrap, 'input').addEventListener('change', function (e) {
        state.internals.topShelf = e.target.checked;
        setState();
      });
      host.appendChild(tsWrap);

    }

    function tweakPanel(i) {
      var sec = state.internals.sections[i];
      var meta = cat.sections[sec.t];
      var box = document.createElement('div');
      box.className = 'wd__tweaks';
      var h = document.createElement('p');
      h.className = 'wd__tweaks-h';
      h.textContent = meta.label;
      box.appendChild(h);

      // swap type
      var swapItems = Object.keys(cat.sections).map(function (k) { return { value: k, label: cat.sections[k].label }; });
      box.appendChild(fieldset('Change this to', radioGroup('swap' + i, swapItems, sec.t, function (item) {
        var t = item.value;
        var nm = cat.sections[t];
        var next = { t: t, w: clamp(sec.w, nm.min, nm.max) };
        if (t === 'drawer') { next.drawers = nm.params.drawers.default; next.hangAbove = nm.params.hangAbove.default; }
        if (t === 'shelf') next.shelves = nm.params.shelves.default;
        state.internals.sections[i] = next;
        redistribute(i);
        state.internals.layout = null;
        setState();
        withFocusKept(function () { rebuild('inside'); });
        announce(nm.label + ' selected');
      })));

      if (sec.t === 'drawer') {
        var dItems = meta.params.drawers.options.map(function (n) { return { value: n, label: n + ' drawers' }; });
        box.appendChild(fieldset('How many drawers', radioGroup('dr' + i, dItems, sec.drawers, function (item) {
          state.internals.sections[i].drawers = item.value;
          setState(); withFocusKept(function () { rebuild('inside'); });
        })));
        var haItems = [
          { value: true, label: meta.params.hangAbove.labels['true'] },
          { value: false, label: meta.params.hangAbove.labels['false'] }
        ];
        box.appendChild(fieldset('Above the tower', radioGroup('ha' + i, haItems, !!sec.hangAbove, function (item) {
          state.internals.sections[i].hangAbove = item.value;
          setState(); withFocusKept(function () { rebuild('inside'); });
        })));
      }
      if (sec.t === 'shelf') {
        var sItems = meta.params.shelves.options.map(function (n) { return { value: n, label: n + ' shelves' }; });
        box.appendChild(fieldset('How many shelves', radioGroup('sh' + i, sItems, sec.shelves, function (item) {
          state.internals.sections[i].shelves = item.value;
          setState(); withFocusKept(function () { rebuild('inside'); });
        })));
      }
      return box;
    }

    /* Keep the sections summing to the opening after a manual change. */
    /* Every section must stay inside its catalogue min/max AND the set must sum
       to the opening exactly. The old version could satisfy the sum by inflating
       a fixed-width tower past its maximum (e.g. a 1100mm drawer tower against a
       800mm max), which then travelled to the CRM as an unbuildable design.
       This repairs both invariants, and is also used to sanitise anything
       arriving from a share link or localStorage. */
    function normaliseSections(secs, W) {
      if (!secs || !secs.length) return secs;

      // 1. every width legal for its own type
      secs.forEach(function (sec) {
        var m = cat.sections[sec.t];
        if (m) sec.w = clamp(sec.w, m.min, m.max);
      });

      // 2. absorb the difference into the flexible bays first, then any bay,
      //    never exceeding a bay's own limits
      function push(diff, flexOnly) {
        for (var pass = 0; pass < 2 && diff; pass++) {
          for (var i = secs.length - 1; i >= 0 && diff; i--) {
            var m = cat.sections[secs[i].t];
            if (!m) continue;
            if (flexOnly && !m.flex) continue;
            var want = clamp(secs[i].w + diff, m.min, m.max);
            diff -= (want - secs[i].w);
            secs[i].w = want;
          }
          if (flexOnly) break;
        }
        return diff;
      }
      var remaining = W - secs.reduce(function (a, s) { return a + s.w; }, 0);
      remaining = push(remaining, true);
      if (remaining) remaining = push(remaining, false);

      // 3. still short or over: add or drop whole bays rather than break a limit
      var guard = 0;
      while (remaining > 0 && guard++ < 12) {
        var t = 'double';
        var m = cat.sections[t];
        var add = Math.min(m.max, Math.max(m.min, remaining));
        secs.push({ t: t, w: add });
        remaining -= add;
        if (remaining > 0) remaining = push(remaining, false);
      }
      guard = 0;
      while (remaining < 0 && secs.length > 1 && guard++ < 12) {
        var dropped = secs.pop();
        remaining += dropped.w;
        remaining = push(remaining, false);
      }
      return secs;
    }

    function redistribute(changedIndex) {
      void changedIndex;
      normaliseSections(state.internals.sections, state.opening.w);
    }

    /* ---- Step 5: extras ---- */
    function mountExtras() {
      var host = q(root, '[data-wd-mount="extras"]');
      host.innerHTML = '';
      var instItems = cat.install.map(function (o) { return { value: o.value, label: o.label, sub: o.sub }; });
      host.appendChild(fieldset('Install', radioGroup('inst', instItems, state.install, function (item) {
        state.install = item.value;
        setState(); withFocusKept(function () { rebuild('extras'); });
      }, { className: 'wd__chips wd__chips--wide' })));
    }

    /* ---------------------------------------------------------
       Steps
       --------------------------------------------------------- */
    /* Every mount re-creates its DOM, which throws focus to <body> and strands
       keyboard users mid-group. Remember what was focused and restore it. */
    function withFocusKept(fn) {
      var active = document.activeElement;
      var key = null;
      if (active && active !== document.body) {
        var group = active.closest ? active.closest('[role="radiogroup"], .wd__seclist, [data-wd-mount]') : null;
        if (group) {
          var peers = qa(group, 'button, select, input');
          key = { mount: group.getAttribute('data-wd-mount') || (group.className || ''), index: peers.indexOf(active) };
        }
      }
      fn();
      if (!key || key.index < 0) return;
      var host = root.querySelector('[data-wd-mount="' + key.mount + '"]') ||
        (function () { var g = qa(root, '[role="radiogroup"], .wd__seclist'); for (var i = 0; i < g.length; i++) if ((g[i].className || '') === key.mount) return g[i]; return null; })();
      if (!host) return;
      var again = qa(host, 'button, select, input');
      var target = again[key.index];
      if (target && typeof target.focus === 'function') target.focus();
    }

    function rebuild(which) {
      if (!which || which === 'size') mountSize();
      if (!which || which === 'depth') mountDepth();
      if (!which || which === 'doors') mountDoors();
      if (!which || which === 'inside') mountInside();
      if (!which || which === 'extras') mountExtras();
      scheduleRender();
    }

    function goStep(n, silent) {
      n = clamp(n, 1, TOTAL_STEPS);
      ui.step = n;
      if (n > ui.maxStep) ui.maxStep = n;

      qa(root, '[data-wd-panel]').forEach(function (p) {
        p.classList.toggle('is-active', p.getAttribute('data-wd-panel') === String(n));
      });
      qa(root, '[data-wd-step-to]').forEach(function (b) {
        var i = +b.getAttribute('data-wd-step-to');
        if (i === n) b.setAttribute('aria-current', 'step'); else b.removeAttribute('aria-current');
        b.setAttribute('data-done', i < ui.maxStep ? '1' : '0');
        b.parentNode.classList.toggle('is-current', i === n);
      });

      if (backBtn) backBtn.hidden = n === 1;
      if (nextBtn) nextBtn.hidden = n === TOTAL_STEPS;

      // Doors step is more useful looking at the doors.
      if (n === 3) { ui.view = 'door'; syncViewButtons(); }
      if (n === 4) { ui.view = 'int'; syncViewButtons(); }

      rebuild();

      if (!silent) {
        var h = q(q(root, '[data-wd-panel="' + n + '"]') || root, '.wd__panel-h');
        if (h) h.focus();
        if (n > 1) track('wd_step', { step: ['', 'size', 'depth', 'doors', 'inside', 'quote'][n] });
        if (n === TOTAL_STEPS && !ui.completeFired) {
          ui.completeFired = true;
          track('wd_design_complete', {
            width: state.opening.w,
            height: state.opening.h,
            door_type: state.doors.type,
            insert: state.doors.insert
          });
        }
        try { history.replaceState(null, '', '#d=' + serialise()); } catch (e) {}
      }
    }

    function syncViewButtons() {
      qa(root, '[data-wd-view]').forEach(function (b) {
        b.setAttribute('aria-pressed', b.getAttribute('data-wd-view') === ui.view ? 'true' : 'false');
      });
    }

    /* ---------------------------------------------------------
       Lead submit
       --------------------------------------------------------- */
    function buildPayload(contact) {
      /* The reference the customer is shown must travel with the lead, or they
         quote "WD-K3Q7" on the phone and the CRM has nothing to match it to. */
      var flags = GEOM.midSupportFlags(state.internals.sections, cat);
      var spec = GEOM.describe(state, cat);
      var g = GEOM.doorGeom(state.opening.w, state.opening.h, state.doors.type, state.doors.count);
      var tType = typeGeom(state.doors.type);
      var ins = findBy(cat.doors.inserts, state.doors.insert);
      var fr = findBy(cat.doors.frames, state.doors.frame);
      var lay = state.internals.layout ? findLayout(state.internals.layout) : null;

      var fields = {
        'Opening width': state.opening.w + 'mm',
        'Opening height': state.opening.h + 'mm',
        'Depth': state.opening.d + 'mm',
        'Door type': tType ? tType.label : state.doors.type,
        'Insert': ins ? ins.label : state.doors.insert,
        'Frame colour': fr ? fr.label : state.doors.frame,
        'Layout': lay ? lay.label : 'Custom',
        'Top shelf': state.internals.topShelf ? 'Yes' : 'No',
        'Install': state.install ? 'Supply and install' : 'Supply only',
        'Suburb': contact.suburb || '',
        'Marketing opt-in': contact.marketing ? 'Yes' : 'No',
        'Design link': shareUrl()
      };
      if (g && g.n) fields['Panels'] = g.n + ' at ' + Math.round(g.pw) + ' × ' + Math.round(g.ph) + 'mm';
      if (flags.midSupport) fields['Mid support required'] = 'Yes';
      if (contact.ref) fields['Reference'] = contact.ref;

      var elapsed = ui.startedAt ? Date.now() - ui.startedAt : 0;

      return {
        name: contact.name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        message: spec + (contact.notes ? '\n\n' + contact.notes : ''),
        design: JSON.parse(JSON.stringify(state)),
        fields: fields,
        source: 'wardrobe-designer:' + location.pathname,
        meta: {
          page: location.pathname,
          captured: 'wardrobe_designer',
          attr: readAttr(),
          suspected_bot: (elapsed > 0 && elapsed < 20000) || !!contact.hp
        }
      };
    }

    function send(contact) {
      var body, headers;
      /* Anything that throws synchronously here must still come back as a
         rejected promise, otherwise the caller's .catch never runs and the
         submit button stays disabled forever. */
      try {
        body = JSON.stringify(buildPayload(contact));
        headers = { 'Content-Type': 'application/json' };
        if (crmKey) headers['x-goober-key'] = crmKey;
      } catch (e) {
        return Promise.reject(e);
      }

      /* fetch + keepalive, never sendBeacon: sendBeacon cannot carry the
         x-goober-key header. XHR is the fallback for the same reason. */
      if (typeof window.fetch === 'function') {
        try {
          return window.fetch(endpoint, { method: 'POST', headers: headers, body: body, keepalive: true })
            .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r; });
        } catch (e) {
          return Promise.reject(e);
        }
      }
      return new Promise(function (resolve, reject) {
        try {
          var x = new XMLHttpRequest();
          x.open('POST', endpoint, true);
          for (var k in headers) if (Object.prototype.hasOwnProperty.call(headers, k)) x.setRequestHeader(k, headers[k]);
          x.onload = function () { (x.status >= 200 && x.status < 300) ? resolve(x) : reject(new Error('http ' + x.status)); };
          x.onerror = function () { reject(new Error('network')); };
          x.send(body);
        } catch (e) { reject(e); }
      });
    }

    function makeRef() {
      var chars = 'ACDEFGHJKLMNPQRTUVWXY3479';
      var s = '';
      for (var i = 0; i < 4; i++) s += chars.charAt(Math.floor(Math.random() * chars.length));
      return 'WD-' + s;
    }

    if (form) {
      var submitBtn = q(form, '[data-wd-submit]');
      var msg = q(form, '[data-wd-formmsg]');

      function fieldErr(name, text) {
        var e = q(form, '[data-wd-err="' + name + '"]');
        if (e) e.textContent = text || '';
        var input = form.elements[name];
        if (input) {
          if (text) input.setAttribute('aria-invalid', 'true');
          else input.removeAttribute('aria-invalid');
        }
      }
      function valueOf(n) { return (form.elements[n] && form.elements[n].value || '').trim(); }
      function checkOne(n) {
        var v = valueOf(n);
        if (!v) return S.requiredMessage;
        if (n === 'phone' && !/^(\+?61|0)4\d{8}$/.test(v.replace(/[\s()-]/g, ''))) return S.phoneMessage;
        if (n === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) return S.emailMessage;
        return '';
      }
      function validateField(n) {
        var msg = checkOne(n);
        fieldErr(n, msg);
        return !msg;
      }
      function validate() {
        var vals = {
          name: (form.elements.name.value || '').trim(),
          phone: (form.elements.phone.value || '').trim(),
          email: (form.elements.email.value || '').trim(),
          suburb: (form.elements.suburb.value || '').trim()
        };
        var firstBad = null;
        function bad(k, m) { fieldErr(k, m); if (!firstBad) firstBad = form.elements[k]; }

        if (!vals.name) bad('name', S.requiredMessage); else fieldErr('name', '');
        if (!vals.phone) bad('phone', S.requiredMessage);
        else if (!/^(\+?61|0)4\d{8}$/.test(vals.phone.replace(/[\s()-]/g, ''))) bad('phone', S.phoneMessage);
        else fieldErr('phone', '');
        if (!vals.email) bad('email', S.requiredMessage);
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(vals.email)) bad('email', S.emailMessage);
        else fieldErr('email', '');
        if (!vals.suburb) bad('suburb', S.requiredMessage); else fieldErr('suburb', '');

        return { ok: !firstBad, firstBad: firstBad, vals: vals };
      }

      /* Validate only the field being left, so filling the first field does not
         light up errors on fields the customer has not reached yet. */
      var touched = {};
      ['name', 'phone', 'email', 'suburb'].forEach(function (n) {
        var input = form.elements[n];
        if (!input) return;
        input.addEventListener('blur', function () {
          touched[n] = true;
          validateField(n);
        });
        input.addEventListener('input', function () {
          if (touched[n]) validateField(n);
        });
      });

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (msg) msg.hidden = true;
        var v = validate();
        if (!v.ok) { if (v.firstBad) v.firstBad.focus(); return; }
        if (submitBtn.disabled) return;

        var original = submitBtn.textContent;
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-busy', 'true');
        submitBtn.textContent = S.sendingLabel || 'Sending…';

        var contact = {
          name: v.vals.name,
          phone: v.vals.phone,
          email: v.vals.email,
          suburb: v.vals.suburb,
          notes: (form.elements.notes.value || '').trim(),
          marketing: !!(form.elements.marketing && form.elements.marketing.checked),
          hp: (form.elements.company && form.elements.company.value) ? true : false
        };

        function restoreButton() {
          submitBtn.disabled = false;
          submitBtn.removeAttribute('aria-busy');
          submitBtn.textContent = original;
        }

        var ref = makeRef();
        contact.ref = ref;
        send(contact).then(function () {

          /* This page confirms in place with no navigation, so firing the
             conversion here is safe: there is no unload to race. */
          try {
            if (typeof window.gtag === 'function') {
              window.gtag('set', 'user_data', { email: contact.email, phone_number: contact.phone });
            }
          } catch (err) {}
          track('generate_lead', {
            ref: ref,
            door_type: state.doors.type,
            insert: state.doors.insert,
            width: state.opening.w,
            height: state.opening.h,
            door_count: state.doors.count
          });
          if (S.gadsConv) {
            try {
              if (typeof window.gtag === 'function') {
                window.gtag('event', 'conversion', { send_to: S.gadsConv, transaction_id: ref });
              }
            } catch (err) {}
          }

          showDone(contact.name, ref);
        }).catch(function () {
          restoreButton();
          if (msg) { msg.textContent = S.errorMessage || ''; msg.hidden = false; }
        });
      });
    }

    function showDone(name, ref) {
      ui.view = 'door';
      syncViewButtons();
      scheduleRender();

      qa(root, '[data-wd-panel]').forEach(function (p) {
        p.classList.toggle('is-active', p.getAttribute('data-wd-panel') === 'done');
      });
      var steps = q(root, '[data-wd-steps]');
      if (steps) steps.hidden = true;
      if (nav) nav.hidden = true;
      var h = q(root, '[data-wd-done-h]');
      if (h) { h.textContent = tpl(S.doneHeading || '', { name: name }); h.focus(); }
      var refEl = q(root, '[data-wd-ref]');
      if (refEl) refEl.textContent = ref;
      /* Block further writes first: the debounced persist from the repaint
         above would otherwise re-save the design we just submitted. */
      persistBlocked = true;
      if (persistTimer) clearTimeout(persistTimer);
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    }

    /* ---------------------------------------------------------
       Wiring
       --------------------------------------------------------- */
    qa(root, '[data-wd-view]').forEach(function (b) {
      b.addEventListener('click', function () {
        ui.view = b.getAttribute('data-wd-view');
        syncViewButtons();
        scheduleRender();
      });
    });

    if (nextBtn) nextBtn.addEventListener('click', function () { goStep(ui.step + 1); });
    if (backBtn) backBtn.addEventListener('click', function () { goStep(ui.step - 1); });
    qa(root, '[data-wd-step-to]').forEach(function (b) {
      b.addEventListener('click', function () {
        var target = +b.getAttribute('data-wd-step-to');
        if (target <= ui.maxStep) goStep(target);
      });
    });

    /* Tapping the drawing selects a section and jumps to its tweaks. */
    (q(root, '[data-wd-face]') || frame).addEventListener('click', function (e) {
      var hit = e.target.closest ? e.target.closest('[data-sec]') : null;
      if (!hit) return;
      var i = parseInt(hit.getAttribute('data-sec'), 10);
      if (isNaN(i)) return;
      ui.selected = (ui.selected === i) ? -1 : i;
      if (ui.step !== 4) goStep(4); else rebuild('inside');
      scheduleRender();
    });

    var restoreBar = q(root, '[data-wd-restore]');
    var restarted = q(root, '[data-wd-restart]');
    if (restarted) {
      restarted.addEventListener('click', function () {
        try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
        state = defaultState();
        ui.selected = -1;
        ui.maxStep = 1;
        ui.view = 'int';
        ui.started = false;
        spin = 0;
        syncViewButtons();
        if (restoreBar) restoreBar.hidden = true;
        try { history.replaceState(null, '', location.pathname); } catch (e) {}
        goStep(1);
      });
    }

    var didRestore = restore();
    if (didRestore && restoreBar) restoreBar.hidden = false;

    syncViewButtons();
    goStep(1, true);
    paint();
  }

  function boot() {
    qa(document, '[data-wd-root]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
